import { features } from 'config/features';
import { useCallback } from 'react';
import assignmentService from 'services/assignment';
import courseService from 'services/course';
import { assignmentFormToApi } from 'utils/assignmentDefaults';
import { resolveAssignmentDraftState } from 'utils/assignmentPersistencePolicy';

const useAssignmentPersistence = ({
    auth,
    courseId,
    creationIdRef,
    persistedAssignmentRef,
    persistedAssignment,
    restrictions,
    setPersistedAssignment,
    setAssignmentInApp,
    removeAssignmentInApp,
    derivedDataCleanup,
    onPersisted,
}) => {
    const persist = useCallback(
        async ({ snapshot, intent, apiData, rubric }) => {
            const previous = persistedAssignmentRef.current || persistedAssignment || {};
            const id = previous.id;
            const isDraft = resolveAssignmentDraftState({ intent, persistedAssignment: previous });
            const assignment = assignmentFormToApi(snapshot, {
                mode: id ? 'edit' : 'create',
                assignmentId: creationIdRef.current,
                courseId,
                ownerAccountId: auth?.id,
                apiData,
                rubric,
                isDraft,
                existing: {
                    ...previous,
                    has_finished_preview:
                        previous.has_finished_preview ??
                        (!features.enableFinetuning && Boolean(snapshot.api_assignment_id)),
                },
                restrictions,
            });
            const prepared = derivedDataCleanup.prepare(previous, assignment);
            let saved;
            if (id) {
                saved = await assignmentService.updateAssignment(id, prepared.assignment);
            } else {
                try {
                    saved = await courseService.createCourseAssignment(courseId, prepared.assignment);
                } catch (createError) {
                    try {
                        saved = await assignmentService.getAssignment(creationIdRef.current);
                    } catch (lookupError) {
                        lookupError.cause = createError;
                        throw lookupError;
                    }
                }
            }
            persistedAssignmentRef.current = saved;
            setPersistedAssignment(saved);
            setAssignmentInApp(saved);
            onPersisted?.(saved);
            const cleanupErrors = await derivedDataCleanup.execute({
                assignmentId: id,
                assignment: prepared.assignment,
                previewInvalidated: prepared.previewInvalidated,
                formativeInvalidated: prepared.formativeInvalidated,
            });
            return { assignment: saved, cleanupErrors };
        },
        [
            auth?.id,
            courseId,
            creationIdRef,
            derivedDataCleanup,
            persistedAssignment,
            persistedAssignmentRef,
            restrictions,
            setAssignmentInApp,
            setPersistedAssignment,
            onPersisted,
        ]
    );

    const deleteAssignment = useCallback(
        async (id) => {
            await assignmentService.deleteAssignment(id);
            removeAssignmentInApp(id);
        },
        [removeAssignmentInApp]
    );

    return { deleteAssignment, persist };
};

export default useAssignmentPersistence;
